export const en = {
  nav: {
    links: [
      { href: "#tech", label: "Technology" },
      { href: "#cases", label: "Use Cases" },
      { href: "#roi", label: "ROI Calculator" },
      { href: "#team", label: "Team" },
      { href: "#contact", label: "Contact" },
    ],
    demoBtn: "Request Demo",
  },
  hero: {
    badgeResident: null, // Skolkovo explicitly omitted
    badgeNvidia: "NVIDIA Inception Member",
    badgePatent: "Patented Technology",
    headlinePart1: "The Catalyst for Your",
    headlineGlow: "AI Infrastructure:",
    headlinePart2: "GPU Utilization up to",
    headlineUtil: "85%",
    subheadline:
      "Stop burning 60% of your AI budget on idle silicon. InBoost pairs Reinforcement Learning (RL) and Graph Neural Networks (GNNs) to mathematically map distributed workloads to physical hardware in real time.",
    ctaDemo: "Request Demo",
    ctaRoi: "Calculate ROI",
    trustStrip: [
      "Datacenter Capacity Expansion",
      "Production-Grade on Kubernetes",
      "Patented Orchestration Technology",
    ],
    graphLabel: "Bipartite Matching · RT",
    graphLive: "LIVE",
    graphFormula: "Σ Score = CEI × (1 − ZCR) → policy:",
  },
  crisis: {
    sectionNum: "/ 01 — Infrastructure Bottleneck",
    headlinePart1: "Your AI Cluster is Running",
    headlinePart2: "at One-Third Capacity.",
    subtext:
      "Default Kubernetes schedulers are blind to GPU physics. They see a generic card—not the workload topology. The result: millions of dollars burned on idle H100s/H200s, thermal throttling, and stranded memory.",
    stats: [
      {
        value: 35,
        title: "Average GPU Utilization",
        body: "Real-world baseline across enterprise clusters running standard Kubernetes and default gang schedulers.",
        accent: "#E11D48",
      },
      {
        value: 67,
        title: "Compute Budget Wasted",
        body: "Lost to memory fragmentation, suboptimal pod gang-scheduling, and stranded Streaming Multiprocessors (SMs).",
        accent: "#E0A800",
      },
      {
        value: 53,
        title: "Direct TCO Reduction",
        body: "Achieved by deploying InBoost AI Engine on your existing GPU fleet—zero hardware replacements required.",
        accent: "#0099C9",
      },
    ],
  },
  techCore: {
    sectionNum: "/ 02 — Algorithmic Core",
    headlinePart1: "Computational Darwinism",
    headlinePart2: "for Kubernetes.",
    subtext:
      "We replace static heuristic scheduling with an autonomous intelligence engine powered by RL + GNNs. Every workload and GPU is dynamically weighted—only the most efficient allocation survives.",
    scoringTitle: "Scoring Function",
    formula: {
      left: "Score =",
      cei: "CEI",
      mul: "× (1 −",
      zcr: "ZCR",
      right: ")",
    },
    factors: {
      ceiTitle: "CEI · Compute Efficiency Index",
      ceiDesc:
        "Measures physical silicon execution: T_ideal / T_actual. Accounts for PCIe throughput, thermal throttling, and NVLink mesh topology.",
      zcrTitle: "ZCR · Zonal Contention Ratio",
      zcrDesc:
        "Controls zone saturation across clusters, preventing bandwidth bottlenecks and memory fragmentation.",
    },
    agentTitle: "RL Policy Agent",
    agentSub: "Reinforcement Learning",
    agentDesc:
      "Trained on real-time DCGM telemetry and continuously adapting to heterogeneous workloads. Reward = Throughput × Energy Efficiency.",
    gnnTitle: "GNN Embedding",
    gnnSub: "Graph Neural Network",
    gnnDesc:
      "Encodes the cluster as a weighted bipartite graph (Workload ↔ Silicon), taking NUMA nodes, NVLink domains, and network fabrics into account.",
  },
  useCases: {
    sectionNum: "/ 03 — Economic Impact",
    headlinePart1: "Three Architectural Levers",
    headlinePart2: "That Transform Cluster Economics.",
    cases: [
      {
        id: "case-hetero",
        title: "Heterogeneous Clusters",
        overline: "Hardware Mix",
        body: "Deploy consumer-grade RTX 4090 / 5090 cards with enterprise-grade stability at 1/5th the capital cost. InBoost abstracts architectural variance, uniting NVIDIA, AMD, and custom accelerators into a seamless compute fabric.",
        badge: "Multi-Vendor · 5× Capital Efficiency",
        color: "#0099C9",
      },
      {
        id: "case-finops",
        title: "FinOps & Automated Quotas",
        overline: "Cost Governance",
        body: "Granular visibility across engineering teams, predictive capacity planning, and automated fair-share budgeting. Attribute costs precisely down to individual pods and distributed training jobs.",
        badge: "−53% Total Cost of Ownership",
        color: "#0050E6",
      },
      {
        id: "case-integration",
        title: "Enterprise Stack Integration",
        overline: "Enterprise Stack",
        body: "Deep integration with enterprise ERP, CRM, BI, billing, and datacenter DCIM platforms. A unified source of truth for cluster utilization, power draw, cloud spend, and strict SLA compliance.",
        badge: "ERP · CRM · BI · DCIM",
        color: "#00B85A",
      },
    ],
  },
  beforeAfter: {
    sectionNum: "/ 04 — Impact & Evolution",
    headlinePart1: "Same Silicon Fleet.",
    headlinePart2: "Two Radically Different Businesses.",
    headlinePart3: "Technology Stack.",
    beforeLabel: "Standard K8s*",
    beforeNote: "Default scheduler",
    beforeItems: [
      "Blind allocations disregarding hardware topology",
      "HBM memory fragmentation and idle SM units",
      "Unpredictable queue latency and violated SLAs",
      "Runaway cloud bills and datacenter power waste",
    ],
    afterLabel: "InBoost AI Engine",
    afterNote: "RL + GNN scheduler",
    afterItems: [
      "Allocations governed by argmax(CEI × (1 − ZCR))",
      "Dense packing of MIG partitions and NVLink domains",
      "Predictive scheduling with guaranteed SLA enforcement",
      "Up to 53% TCO reduction without purchasing new hardware",
    ],
    evolutionOverline: "Scheduler Evolution · InBoost Closes the Gap",
    evolutionTitle: "From Blind Allocations to Autonomous AI Orchestration.",
    tiers: [
      {
        tier: "Standard K8s*",
        level: "Blind Heuristics",
        desc: "Default scheduler. Blind to hardware topology and NVLink constraints—allocates blindly.",
        color: "#E11D48",
      },
      {
        tier: "Run:AI* (NVIDIA* KAI) / Volcano.sh*",
        level: "Rule-Based Optimization",
        desc: "Manual static rules and priorities. Better than default, but lacks real-time workload awareness.",
        color: "#E0A800",
      },
      {
        tier: "InBoost AI",
        level: "Autonomous AI Orchestration",
        desc: "RL + GNN on DCGM telemetry. Real-time autonomous optimization and topology awareness.",
        color: "#0050E6",
        dotColor: "#0099C9",
        highlight: true,
      },
    ],
  },
  socialProof: {
    sectionNum: "/ 05 — Proven Production Impact",
    headlinePart1: "Metrics Built for Your",
    headlinePart2: "CFO.",
    caseStudyTag: "Case study · Production cluster",
    caseValue: "$13.3M",
    casePerYear: "/ year",
    caseDesc:
      "Annual savings achieved across a 1,024 GPU production cluster by elevating utilization from 38% to 84% while eliminating HBM memory fragmentation.",
    stats: [
      ["1,024", "GPU"],
      ["84%", "UTIL"],
      ["6 mo", "PAYBACK"],
    ],
    proofs: [
      {
        id: "proof-throughput",
        title: "Throughput Acceleration",
        value: "2.5×",
        body: "Accelerate LLM inference and distributed training on existing hardware with zero capital expansion.",
      },
      {
        id: "proof-patents",
        title: "Patented Core Technology",
        value: "DeepTech IP",
        body: "Protected by international deep-tech patents covering autonomous workload-to-hardware matching.",
      },
      {
        id: "proof-mcp",
        title: "Native MCP Integration",
        value: "Native",
        body: "Empower autonomous AI engineering agents to inspect, provision, and govern infrastructure via Model Context Protocol.",
      },
    ],
    partnersHeader: "Ecosystem Compatibility & Standards",
    // Note: Skolkovo is removed from English marquee
    marqueeItems: [
      "NVIDIA DCGM",
      "Kubernetes",
      "Kueue",
      "MIG",
      "NVLink",
      "InfiniBand",
      "Model Context Protocol",
      "NVIDIA Inception",
      "Prometheus",
      "Grafana",
      "Triton Inference Server",
    ],
  },
  videos: {
    sectionNum: "/ 06 — Architecture & Live Demo",
    headlinePart1: "Autonomous Orchestration",
    headlinePart2: "in Action.",
    desc: "Discover how the InBoost RL agent dynamically optimizes heterogeneous GPU workloads in real time, preventing memory fragmentation and enforcing strict SLAs.",
    walkthroughTitle: "Technical Architecture & Live Telemetry",
    walkthroughDesc: "Schedule an interactive deep-dive with our infrastructure architects. We'll simulate your cluster topology and run live DCGM workload profiling.",
    ctaSchedule: "Book Architecture Deep-Dive",
  },
  partners: {
    sectionNum: "/ 07 — Ecosystem & Alliances",
    headlinePart1: "Technology Alliances",
    headlinePart2: "Driving Enterprise Scale.",
    desc: "Collaborative engineering, validated architectures, and strategic integrations across enterprise Kubernetes distributions, high-density GPU servers, and datacenter DCIM platforms.",
  },
  roi: {
    sectionNum: "/ 08 — ROI Modeling",
    headlinePart1: "How Much Compute Budget",
    headlinePart2: "Are You Burning Every Hour?",
    desc: "Adjust your infrastructure parameters in real time. Our server-side simulation models depreciation, power density, and MIG partition efficiency.",
    gpuCountLabel: "GPU Fleet Size",
    gpuCountUnit: "cards",
    gpuCountHint: "16 → 4,096 GPUs",
    utilLabel: "Current GPU Utilization",
    utilHint: "10% → 80%",
    costLabel: "Average Cost per GPU Hour",
    costUnit: "/ hr",
    costHint: "$0.50 → $6.00",
    computeBtn: "Run Simulation",
    computeBtnLoading: "Simulating…",
    resultAnnualSavings: "Annual Projected Savings",
    resultFleetNote: "on your existing GPU fleet",
    resultUtilTarget: "Target Utilization",
    resultCapacityUnlock: "Capacity Unlocked",
    ctaFullReport: "Request Full Financial Analysis →",
  },
  team: {
    sectionNum: "/ 09 — Leadership",
    headlinePart1: "Hardware Architects.",
    headlinePart2: "One Enterprise Mission.",
    members: [
      {
        name: "Olga Andrianova",
        role: "CEO & Co-founder",
        bullets: [
          "17 years at Intel — Enterprise Systems & Strategic Growth Leadership",
          "Former Director of Enterprise AI Initiatives at Sber",
          "Track record scaling mission-critical B2B technology across EMEA",
        ],
        tag: "ex-Intel · Enterprise Leadership",
      },
      {
        name: "Dmitry Bukod",
        role: "CTO & Co-founder",
        bullets: [
          "20 years at Intel — HW/SW co-design & optimization architecture",
          "Ph.D. in Physics & Mathematics",
          "Author and inventor of 3 international patents",
        ],
        tag: "ex-Intel · Ph.D. Physics & Math",
      },
      {
        name: "Andrey",
        role: "CPO",
        bullets: [
          "10+ years across international BigTech and high-growth ventures",
          "Product scale and go-to-market across EMEA and APAC regions",
          "Specialized in Deep Tech, developer tooling, and enterprise platforms",
        ],
        tag: "BigTech · Product Strategy",
      },
      {
        name: "Maxim Maslov",
        role: "Advisor & Angel Investor",
        bullets: [
          "Co-founder & CTO — Eltechs (acquired deep-tech venture)",
          "Huawei (3 years) — Principal Systems Architect",
          "GPU Systems Architect (Baikal Electronics, Astra Group)",
        ],
        tag: "ex-Eltechs · GPU Architect",
      },
      {
        name: "Anton Shokolov",
        role: "AI Research & Engineering",
        bullets: [
          "Novosibirsk State University",
          "Advanced research in Reinforcement Learning and systems engineering",
        ],
        tag: "AI Research · Systems",
      },
    ],
  },
  quizCta: {
    badge: "5 Questions · 60 Seconds",
    headlinePart1: "Assess Your Cluster's",
    headlinePart2: "Potential in 1 Minute.",
    desc: "Answer 5 brief questions regarding your GPU fleet to receive an individualized readiness assessment and a 30-day proof-of-concept roadmap.",
    btnQuiz: "Start Assessment",
    btnRoi: "ROI Calculator",
    deliverablesHeader: "What You Will Receive",
    deliverables: [
      "Cluster orchestration readiness scorecard",
      "Projected 12-month dollar savings model",
      "Structured 30-day pilot roadmap",
      "Benchmarking against top industry peers",
    ],
  },
  quizModal: {
    title: "InBoost Optimization Assessment",
    subtitle: "5 Questions · ~60 Seconds",
    stepContact: "Step 6 · Contact Details",
    stepCounter: (step, total) => `Step ${step + 1} of ${total}`,
    contactHeader: "Where should we send your analysis?",
    contactDesc: "We will compile a tailored report including ROI projections and a 30-day pilot roadmap.",
    fieldCompany: "Company *",
    fieldName: "Full Name *",
    fieldEmail: "Work Email *",
    btnSubmit: "Receive Custom Report",
    btnSubmitting: "Analyzing…",
    successTitle: "Assessment Complete!",
    successReady: "High Optimization Potential",
    successDesc: "We have processed your answers and will deliver a comprehensive report with a 30-day pilot roadmap.",
    btnDemo: "Book Technical Demo",
    btnClose: "Close",
  },
  demoModal: {
    title: "Request InBoost Technical Demo",
    subtitle: "30-Minute Architecture Deep-Dive",
    fieldName: "Full Name *",
    fieldCompany: "Company *",
    fieldEmail: "Work Email *",
    fieldPhone: "Phone",
    fieldGpu: "GPU Fleet Size",
    fieldGpuPlaceholder: "e.g. 256",
    fieldMessage: "Message / Workload Details",
    fieldMessagePlaceholder: "Describe your cluster workload — H100s, RTX 5090s, training vs inference…",
    btnSubmit: "Submit Request",
    btnSubmitting: "Sending…",
    successTitle: "Thank You!",
    successDesc: "We have received your request. An infrastructure architect will reach out within 24 hours.",
    btnClose: "Close",
  },
  footer: {
    taglinePart1: "Bridging the gap between",
    taglineCyan: "business ambition",
    taglinePart2: "and",
    taglineMuted: "silicon reality.",
    badgePatent: "Patented Technology",
    contactsTitle: "Contact",
    notice:
      "This website is informational and provides an overview of the InBoost AI Engine platform, architecture, and deployment models. For enterprise inquiries, please contact our team directly.",
    privacyLink: "Privacy Policy",
    legalLink: "Legal Information",
    copyright: "InBoost AI Engine. All rights reserved.",
    madeIn: "InBoost AI Engine · v1.0",
    trademarks:
      "* All product and technology names and logos (Kubernetes, K8s, NVIDIA, RTX, NVLink, DCGM, MIG, Run:AI, NVIDIA KAI, Volcano.sh, etc.) are trademarks or registered trademarks of their respective owners.",
  },
};
